<template>
  <div class="feature-tab">
    <!-- Profile list -->
    <template v-if="!showForm">
      <div class="feature-tab__head">
        <div>
          <h2 class="feature-tab__title">{{ (options.Lang && options.Lang.CheckoutProfiles) || 'Checkout profiles' }}</h2>
          <p class="feature-tab__subtitle">
            {{ (options.Lang && options.Lang.checkoutProfilesHint) || 'Reusable checkout configuration (texts, questions, emails, PDF) that you attach to one or more brandings.' }}
          </p>
        </div>
        <md-button class="md-raised md-primary" @click="editProfile('neu')">
          <md-icon>add</md-icon>
          {{ (options.Lang && options.Lang.newCheckoutProfile) || 'New profile' }}
        </md-button>
      </div>

      <div v-if="renderComp" class="profile-list">
        <p v-if="!profiles.length" class="empty-state">
          {{ (options.Lang && options.Lang.noCheckoutProfiles) || 'No checkout profiles yet. Create one to get started.' }}
        </p>

        <div v-for="profile in profiles" :key="profile.id" class="profile-card">
          <div class="profile-card__main">
            <md-icon class="profile-card__icon">shopping_cart</md-icon>
            <div class="profile-card__info">
              <span class="profile-card__name">{{ profile.name }}</span>
              <div class="profile-card__meta">
                <span class="chip" v-if="recipients(profile)">
                  <md-icon>mail_outline</md-icon>{{ recipients(profile) }}
                </span>
                <span class="chip" v-if="profile.send_pdf_to_buyer">
                  <md-icon>picture_as_pdf</md-icon>PDF
                </span>
                <span class="chip chip--muted">
                  <md-icon>apartment</md-icon>
                  {{ profile.branding_count }} {{ (options.Lang && options.Lang.brandingsUsing) || 'brandings' }}
                </span>
              </div>
            </div>
          </div>
          <div class="profile-card__actions">
            <md-button class="md-icon-button" @click="editProfile(profile.id)" :title="(options.Lang && options.Lang.Bearbeiten) || 'Edit'">
              <md-icon>edit</md-icon>
            </md-button>
            <md-button class="md-icon-button" @click="deleteProfile(profile)" :title="(options.Lang && options.Lang.löschen) || 'Delete'">
              <md-icon>delete</md-icon>
            </md-button>
          </div>
        </div>
      </div>

      <Success :options="options" />
    </template>

    <!-- Editor (inline, same convention as branding) -->
    <CheckoutProfileForm
        v-else
        :options="options"
        :id="profileId"
        @close="onFormClose"
        @saved-stay="onFormSavedStay" />

    <DeleteConfirmation
        :showConfirm.sync="showDelete"
        :confirmTitle="(options.Lang && options.Lang.deleteCheckoutProfile) || 'Delete profile'"
        :confirmMessage="deleteMessage"
        :trans="options.Lang"
        @confirm="confirmDelete" />
  </div>
</template>

<script>
import Success from '../../components/dialogs/Success.vue'
import DeleteConfirmation from '../../components/modals/DeleteConfirmation.vue'
import CheckoutProfileForm from '../../components/dialogs/editCheckoutProfile.vue'

export default {
  name: 'CheckoutFeatureTab',
  components: { Success, DeleteConfirmation, CheckoutProfileForm },
  props: {
    options: { type: Object, required: true }
  },
  data() {
    return {
      profiles: [],
      renderComp: false,
      showForm: false,
      profileId: null,
      showDelete: false,
      profileToDelete: null,
    }
  },
  created() {
    this.loadProfiles();
  },
  computed: {
    deleteMessage() {
      if (!this.profileToDelete) return '';
      if (this.profileToDelete.branding_count > 0) {
        return (this.options.Lang && this.options.Lang.checkoutProfileInUse)
            || 'This profile is still attached to one or more brandings and cannot be deleted.';
      }
      return (this.options.Lang && this.options.Lang.deleteCheckoutProfileConfirm)
          || 'Do you really want to delete this checkout profile?';
    }
  },
  methods: {
    recipients(profile) {
      return [profile.reservation_email_1, profile.reservation_email_2].filter(Boolean).join(', ');
    },
    loadProfiles() {
      this.renderComp = false;
      const self = this;
      self.profiles = [];
      self.doPost({}, 'checkout-profiles', function (res) {
        if (res.data.status === 'error') {
          self.showErrorMsg(self.$t(res.msg));
        } else {
          self.profiles = (res.data.data || []).map(p => ({
            ...p,
            send_pdf_to_buyer: !!p.send_pdf_to_buyer,
          }));
          self.renderComp = true;
        }
      });
    },
    editProfile(id) {
      this.profileId = id === 'neu' ? null : id;
      this.showForm = true;
    },
    onFormClose() {
      this.showForm = false;
      this.profileId = null;
    },
    /** Save succeeded but the editor stays open (toast shown inside it). Refresh the
     *  underlying list silently so it's current when the user returns via Close. */
    onFormSavedStay() {
      this.loadProfiles();
    },
    deleteProfile(profile) {
      this.profileToDelete = profile;
      this.showDelete = true;
    },
    confirmDelete() {
      const self = this;
      if (!this.profileToDelete) return;
      if (this.profileToDelete.branding_count > 0) {
        self.showErrorMsg((self.options.Lang && self.options.Lang.checkoutProfileInUse)
            || 'This profile is still attached to one or more brandings. Detach it first.');
        this.showDelete = false;
        return;
      }
      const data = { id: this.profileToDelete.id, user_id: this.options.user.id };
      self.doPost({ data }, 'checkout-profiles/delete', function (res) {
        self.showDelete = false;
        if (res.data.status === 'error') {
          // 'error_invalid_request' from a 409 = still attached to brandings → show
          // the helpful "detach first" message rather than a generic error string.
          const inUse = res.msg === 'error_invalid_request'
            || (res.data && res.data.blockedBy && res.data.blockedBy.length);
          self.showErrorMsg(inUse
            ? ((self.options.Lang && self.options.Lang.checkoutProfileInUse)
                || 'This profile is still attached to one or more brandings. Detach it first.')
            : self.$t(res.msg));
        } else {
          self.showSuccessMsg((self.options.Lang && self.options.Lang.checkoutProfileDeleted) || 'Profile deleted');
          self.loadProfiles();
        }
      });
    },
  }
};
</script>

<style scoped>
.feature-tab { padding: 1.5rem 0.5rem; }

.feature-tab__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.feature-tab__title { font-size: 1.4rem; font-weight: 600; margin: 0; color: #2c2c2c; }
.feature-tab__subtitle { font-size: 0.85rem; color: #888; margin: 0.25rem 0 0; max-width: 640px; }

.profile-list { display: flex; flex-direction: column; gap: 0.85rem; }

.empty-state {
  padding: 2.5rem;
  text-align: center;
  color: #999;
  background: #fafafa;
  border-radius: 1rem;
  font-style: italic;
}

.profile-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #fff;
  border-radius: 1rem;
  padding: 1rem 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  transition: box-shadow .18s ease, transform .18s ease;
}
.profile-card:hover { box-shadow: 0 4px 18px rgba(0, 0, 0, 0.10); }

.profile-card__main { display: flex; align-items: center; gap: 1rem; min-width: 0; }
.profile-card__icon { color: #b0b0b0; }
.profile-card__info { min-width: 0; }
.profile-card__name { font-size: 1.05rem; font-weight: 500; color: #2c2c2c; display: block; }

.profile-card__meta { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.35rem; }
.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: #555;
  background: #f1f3f5;
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
}
.chip .md-icon { font-size: 14px !important; color: #888; }
.chip--muted { color: #999; }

.profile-card__actions { display: flex; flex-shrink: 0; }
</style>
